import { useState } from 'react';
import type { Device, List } from '@shared/types.ts';
import { formatRelative, formatStamp } from '@shared/time.ts';
import { CHANGELOG } from '../changelog.ts';
import { APP_COMMIT, APP_VERSION, BUILT_AT } from '../lib/version.ts';
import { deleteList, moveList, renameDevice, renameList, setDefaultList } from '../store/state.ts';
import { deviceId, isIos, isStandalone } from '../lib/device.ts';
import { shareLink } from '../lib/token.ts';
import { setTheme, type ThemeChoice } from '../lib/theme.ts';
import { useTheme } from '../hooks/useStore.ts';
import PlatformIcon from './PlatformIcon.tsx';

interface Props {
  lists: List[];
  devices: Device[];
  onClose: () => void;
}

export default function Settings({ lists, devices, onClose }: Props) {
  const link = shareLink();
  const [copied, setCopied] = useState(false);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label="Réglages">
      <header className="sheet__header">
        <h2>Réglages</h2>
        <button type="button" className="button button--ghost" onClick={onClose}>
          Fermer
        </button>
      </header>

      {!isStandalone() && <InstallHint />}

      <section className="sheet__section">
        <h3>Apparence</h3>
        <ThemePicker />
      </section>

      <section className="sheet__section">
        <h3>Listes</h3>
        <ul className="settings-list">
          {lists.map((list, index) => (
            <ListSettingsRow
              key={list.id}
              list={list}
              index={index}
              total={lists.length}
              canDelete={lists.length > 1}
            />
          ))}
        </ul>
      </section>

      <section className="sheet__section">
        <h3>Appareils</h3>
        <ul className="settings-list">
          {devices.map((device) => (
            <DeviceSettingsRow key={device.id} device={device} />
          ))}
        </ul>
      </section>

      <section className="sheet__section">
        <h3>Version</h3>
        <VersionInfo />
      </section>

      <section className="sheet__section">
        <h3>Lien du foyer</h3>
        <p className="sheet__note">
          Ce lien <strong>est</strong> la clé du foyer : qui l’a peut lire et modifier les listes. Ne le
          transmets qu’à l’autre appareil. Si un téléphone perd ses données, c’est par ce lien qu’il
          retrouve l’accès.
        </p>

        {link && (
          <>
            <code className="sheet__link">{link}</code>
            <button
              type="button"
              className="button"
              onClick={() => {
                void navigator.clipboard?.writeText(link).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }}
            >
              {copied ? 'Copié' : 'Copier le lien'}
            </button>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * Version réellement exécutée sur cet appareil, et historique.
 *
 * L'application étant une PWA, le service worker continue de servir la version
 * installée tant que la mise à jour n'a pas été acceptée : deux téléphones
 * peuvent donc tourner sur des versions différentes. Ce que cet écran affiche
 * est celle **de cet appareil**, ce qui permet de vérifier sans deviner qu'un
 * déploiement est bien arrivé jusqu'ici.
 */
function VersionInfo() {
  const [openVersion, setOpenVersion] = useState<string | null>(CHANGELOG[0]?.version ?? null);

  return (
    <>
      <p className="version__current">
        <strong>Version {APP_VERSION}</strong>
        <span className="version__build">
          {BUILT_AT !== null && <>Installée le {formatStamp(BUILT_AT)}</>}
          {APP_COMMIT !== null && <> · build {APP_COMMIT}</>}
        </span>
      </p>

      <ul className="version-list">
        {CHANGELOG.map((release) => {
          const open = openVersion === release.version;

          return (
            <li key={release.version} className="version-list__item">
              <button
                type="button"
                className="version-list__toggle"
                aria-expanded={open}
                onClick={() => setOpenVersion(open ? null : release.version)}
              >
                <span className="version-list__name">
                  {release.version}
                  {release.version === APP_VERSION && (
                    <span className="version-list__badge">installée</span>
                  )}
                </span>
                <span className="version-list__date">{formatReleaseDate(release.date)}</span>
                <span className="version-list__chevron" aria-hidden="true">
                  {open ? '▾' : '▸'}
                </span>
              </button>

              {open && (
                <ul className="version-list__changes">
                  {release.changes.map((change) => (
                    <li key={change}>{change}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** « 2026-08-23 » → « 23 août 2026 ». Fuseau Europe/Paris, comme partout. */
function formatReleaseDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;

  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Paris',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
}

/**
 * Choix du thème, propre à chaque téléphone.
 *
 * Ce réglage n'est PAS synchronisé : c'est une préférence d'affichage, pas une
 * donnée du foyer. La partager reviendrait à laisser un téléphone imposer son
 * confort de lecture à l'autre.
 */
function ThemePicker() {
  const current = useTheme();

  const options: { value: ThemeChoice; label: string; hint: string }[] = [
    { value: 'light', label: 'Clair', hint: 'Fond blanc, texte noir' },
    { value: 'dark', label: 'Sombre', hint: 'Fond noir, texte blanc' },
    { value: 'auto', label: 'Automatique', hint: 'Suit le réglage du téléphone' },
  ];

  return (
    <>
      <div className="segmented" role="radiogroup" aria-label="Thème">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={current === option.value}
            className={`segmented__option${current === option.value ? ' segmented__option--active' : ''}`}
            onClick={() => setTheme(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <p className="sheet__note">{options.find((o) => o.value === current)?.hint}</p>
    </>
  );
}

/**
 * Sur iOS, ne pas installer expose à la purge des données au bout de 7 jours
 * sans visite (ITP). Ce n'est donc pas un conseil de confort.
 */
function InstallHint() {
  return (
    <aside className="sheet__hint">
      <strong>Installe l’application sur l’écran d’accueil.</strong>
      {isIos() ? (
        <>
          <p>
            Bouton Partager, puis « Sur l’écran d’accueil ». Sans cette installation, iOS efface les
            données de l’application au bout de sept jours sans ouverture — y compris les
            modifications non encore synchronisées.
          </p>
          <p>
            <strong>Copie le lien du foyer avant d’installer</strong> (plus bas sur cet écran). Sur
            iPhone, l’application installée a son propre stockage, séparé de Safari : à sa première
            ouverture elle demandera le lien, qu’il suffira de coller.
          </p>
        </>
      ) : (
        <p>Menu du navigateur, puis « Installer l’application ». L’application démarre alors sans réseau.</p>
      )}
    </aside>
  );
}

function ListSettingsRow({
  list,
  index,
  total,
  canDelete,
}: {
  list: List;
  index: number;
  total: number;
  canDelete: boolean;
}) {
  const [name, setName] = useState(list.name);

  return (
    <li className="settings-row">
      <input
        className="settings-row__input"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={() => name.trim() !== list.name && void renameList(list, name)}
        maxLength={80}
        aria-label={`Nom de la liste ${list.name}`}
      />

      <div className="settings-row__actions">
        <button
          type="button"
          className="button button--small button--ghost"
          disabled={index === 0}
          onClick={() => void moveList(list, index - 1)}
          aria-label={`Monter ${list.name}`}
        >
          ↑
        </button>
        <button
          type="button"
          className="button button--small button--ghost"
          disabled={index === total - 1}
          onClick={() => void moveList(list, index + 1)}
          aria-label={`Descendre ${list.name}`}
        >
          ↓
        </button>
        <button
          type="button"
          className={`button button--small${list.is_default ? '' : ' button--ghost'}`}
          onClick={() => void setDefaultList(list)}
          aria-pressed={list.is_default}
        >
          Par défaut
        </button>
        {canDelete && (
          <button
            type="button"
            className="button button--small button--danger"
            onClick={() => void deleteList(list)}
            aria-label={`Supprimer ${list.name}`}
          >
            Supprimer
          </button>
        )}
      </div>
    </li>
  );
}

function DeviceSettingsRow({ device }: { device: Device }) {
  const [label, setLabel] = useState(device.label);
  const isThisDevice = device.id === deviceId();

  return (
    <li className="settings-row">
      <PlatformIcon platform={device.platform} />
      <input
        className="settings-row__input"
        value={label}
        onChange={(event) => setLabel(event.target.value)}
        onBlur={() => label.trim() !== device.label && void renameDevice(device.id, label)}
        maxLength={80}
        aria-label={`Nom de l’appareil ${device.label}`}
      />
      <span className="settings-row__meta">
        {isThisDevice ? 'cet appareil' : formatRelative(device.created_at)}
      </span>
    </li>
  );
}
